import React, { useMemo, useState, type FC } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Button, DataTable, SegmentedControl, StatusBadge } from "../../../../components/shared";
import FeatureFlagGuard from "../../../FeatureFlagGuard/FeatureFlagGuard";
import { SIDER_TABS } from "../../../../utils/Constants";
import { NAVIGATION_ROUTES } from "../../../../utils/NavigationUtils";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { useListSegments } from "../../../../queries/dynamicBonuses/useListSegments";
import { useListCampaigns } from "../../../../queries/dynamicBonuses/useListCampaigns";
import {
  EMPTY_DISPLAY,
  LOOKBACK_WINDOW_DAYS_LABEL,
  segmentStatusOptions,
} from "../constants";
import type { CampaignOut, SegmentOut, SegmentStatus } from "../types";
import { compareNumbers, compareStrings } from "../listTableSorters";
import CampaignBuilderHeader from "../controls/CampaignBuilderHeader";
import { campaignsUsingSegment } from "./campaignsUsingSegment";
import listStyles from "../../../Affiliates/Affiliates.module.css";
import styles from "../stylesDynamicBonuses.module.css";

const MAX_VISIBLE_CAMPAIGNS = 3;

const STATUS_COLORS: Record<SegmentStatus, string> = {
  active: "#2a7341",
  paused: "#4f62c9",
  archived: "#e74c3c",
};

type SegmentRow = SegmentOut & {
  campaigns: CampaignOut[];
};

const formatStatus = (status: SegmentStatus): string =>
  segmentStatusOptions().find(({ value }) => value === status)?.label ?? status;

const renderCampaigns = (campaigns: CampaignOut[]): React.ReactNode => {
  if (campaigns.length === 0) return EMPTY_DISPLAY;

  const visibleCampaigns = campaigns.slice(0, MAX_VISIBLE_CAMPAIGNS);
  const remainingCount = campaigns.length - visibleCampaigns.length;

  return (
    <div className={styles.segmentUsageCell}>
      <div className={styles.segmentUsageChips}>
        {visibleCampaigns.map((campaign) => (
          <span
            key={campaign.campaign_id}
            className={styles.segmentUsageChip}
            title={campaign.name}
          >
            {campaign.name}
          </span>
        ))}
      </div>
      {remainingCount > 0 && (
        <span className={styles.segmentUsageOverflow}>+{remainingCount}</span>
      )}
    </div>
  );
};

const columns = [
  {
    title: AppStrings.NAME,
    dataIndex: "name",
    key: "name",
    width: 199,
    render: (name: string) => <span className={styles.segmentName}>{name}</span>,
    sorter: (a: SegmentRow, b: SegmentRow) => compareStrings(a.name, b.name),
  },
  {
    title: AppStrings.STATUS,
    dataIndex: "status",
    key: "status",
    width: 100,
    ellipsis: false,
    render: (status: SegmentStatus) => (
      <StatusBadge
        color={STATUS_COLORS[status]}
        variant="solid"
        className={styles.segmentStatusBadge}
      >
        {formatStatus(status)}
      </StatusBadge>
    ),
    sorter: (a: SegmentRow, b: SegmentRow) => compareStrings(a.status, b.status),
  },
  {
    title: AppStrings.PAM_SEGMENTS_ESTIMATED_REACH,
    key: "estimated_reach",
    width: 164,
    render: () => EMPTY_DISPLAY,
  },
  {
    title: LOOKBACK_WINDOW_DAYS_LABEL,
    dataIndex: "membership_ttl_days",
    key: "membership_ttl_days",
    width: 389,
    render: (value: number | null) => value ?? EMPTY_DISPLAY,
    sorter: (a: SegmentRow, b: SegmentRow) =>
      compareNumbers(a.membership_ttl_days, b.membership_ttl_days),
  },
  {
    title: AppStrings.PAM_SEGMENTS_USED_ON,
    dataIndex: "campaigns",
    key: "campaigns",
    width: 280,
    ellipsis: false,
    render: renderCampaigns,
    sorter: (a: SegmentRow, b: SegmentRow) =>
      compareNumbers(a.campaigns.length, b.campaigns.length),
  },
];

type StatusFilter = SegmentStatus | "all";

const getStatusOptions = (): { label: string; value: StatusFilter }[] => [
  { label: AppStrings.ALL, value: "all" },
  ...segmentStatusOptions(),
];

const getStatusFilterItems = () =>
  getStatusOptions().map(({ label, value }) => ({
    id: value,
    label,
  }));

const SegmentsPage: FC = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const { data, isLoading, isError } = useListSegments();
  const { data: campaigns } = useListCampaigns();

  const statusFilterItems = getStatusFilterItems();

  const rows = useMemo<SegmentRow[]>(() => {
    const all = data ?? [];
    const visibleSegments =
      statusFilter === "all"
        ? all
        : all.filter((segment) => segment.status === statusFilter);

    return visibleSegments.map((segment) => ({
      ...segment,
      campaigns: campaignsUsingSegment(campaigns ?? [], segment.segment_id),
    }));
  }, [campaigns, data, statusFilter]);

  return (
    <>
      <FeatureFlagGuard feature="dynamicBonuses" mode="route">
        <div className={`${listStyles.container} ${styles.segmentsContainer}`}>
          <CampaignBuilderHeader
            title={AppStrings.PAM_SEGMENTS_TITLE}
            backAriaLabel={AppStrings.PAM_BACK_TO_CAMPAIGNS}
            onBack={() => navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_CAMPAIGNS)}
          />

          <div className={styles.segmentsToolbar}>
            <SegmentedControl
              items={statusFilterItems}
              value={statusFilter}
              onChange={setStatusFilter}
              ariaLabel={AppStrings.PAM_SEGMENTS_FILTER_ARIA_LABEL}
              className={styles.segmentStatusFilter}
              data-testid="status-filter"
            />

            <Button
              intent="primary"
              iconLeft={<PlusOutlined />}
              className={styles.createSegmentButton}
              onClick={() => navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_SEGMENT_NEW)}
            >
              {AppStrings.CREATE_SEGMENT}
            </Button>
          </div>

          <div className={styles.segmentsTableSection}>
            {isError && (
              <p className={styles.listError}>{AppStrings.PAM_SEGMENTS_LOAD_ERROR}</p>
            )}

            <DataTable<SegmentRow>
              rowKey="segment_id"
              columns={columns}
              dataSource={rows}
              loading={isLoading}
              pagination={false}
              className={styles.segmentsTable}
              onRow={(record) => ({
                onClick: () =>
                  navigate(
                    NAVIGATION_ROUTES.DYNAMIC_BONUS_SEGMENT_DETAIL.replace(
                      ":segmentId",
                      record.segment_id,
                    ),
                  ),
                style: { cursor: "pointer" },
              })}
            />
          </div>
        </div>
      </FeatureFlagGuard>
    </>
  );
};

export default SegmentsPage;
